import { useState } from "react";
import { Link } from "react-router-dom";
import PageContainer, { PageHeader } from "@/components/PageContainer";
import { PRICING_TIERS, PRICING_ADDONS, PRICING_FAQ } from "@/data/content";
import { Check, ArrowRight, ChevronDown } from "lucide-react";

function TierCard({ tier }) {
  return (
    <article
      className={`card p-6 lg:p-7 flex flex-col relative ${
        tier.featured ? "border-teal bg-teal-light/25 shadow-pop lg:-translate-y-2" : ""
      }`}
      data-testid={`pricing-tier-${tier.id}`}
    >
      {tier.featured && (
        <div className="absolute -top-3 left-6 badge badge-navy text-[10px] px-3 py-1" data-testid="pricing-most-popular">
          MOST POPULAR
        </div>
      )}
      <div className="overline mb-2">{tier.tier}</div>
      <h3 className="text-navy text-xl font-semibold mb-3">{tier.name}</h3>
      <div className="mb-4">
        <div className="text-3xl font-semibold text-navy">{tier.price}</div>
        <div className="text-sm text-ink-muted">{tier.cadence}</div>
      </div>

      <ul className="space-y-2 mb-6 flex-1">
        {tier.included.map((it) => (
          <li key={it} className="flex gap-2 text-sm text-ink items-start">
            <Check size={15} strokeWidth={2.5} className="text-teal mt-0.5 flex-shrink-0" />
            <span>{it}</span>
          </li>
        ))}
      </ul>

      <div className="rule mb-4" />
      <div className="text-xs uppercase tracking-wider text-ink-muted mb-1">Best for</div>
      <p className="text-sm text-navy mb-5">{tier.bestFor}</p>

      <Link
        to="/book-call"
        state={{ tier: tier.id }}
        className={tier.featured ? "btn btn-primary" : "btn btn-secondary"}
        data-testid={`pricing-cta-${tier.id}`}
      >
        Book a call <ArrowRight size={14} strokeWidth={2} />
      </Link>
    </article>
  );
}

function ScaleCard({ tier }) {
  return (
    <article className={`card p-6 ${tier.featured ? "border-teal" : ""}`} data-testid={`scale-${tier.id}`}>
      <div className="overline mb-2">{tier.tier}</div>
      <h4 className="text-navy font-semibold mb-1">{tier.name}</h4>
      <div className="text-xs text-ink-muted mb-4 italic">Driven by {tier.scale.driver}.</div>
      <div className="mb-3">
        <div className="text-xs uppercase tracking-wider text-ink-muted mb-1">Low anchor</div>
        <div className="text-sm text-ink leading-snug">{tier.scale.low}</div>
      </div>
      <div className="mb-3">
        <div className="text-xs uppercase tracking-wider text-teal mb-1">High anchor</div>
        <div className="text-sm text-navy leading-snug font-medium">{tier.scale.high}</div>
      </div>
      <div className="rule my-3" />
      <div className="text-xs italic text-ink-muted">{tier.scale.note}</div>
    </article>
  );
}

function FAQItem({ q, a, i }) {
  const [open, setOpen] = useState(i === 0);
  return (
    <div className="card p-0 overflow-hidden" data-testid={`faq-${i}`}>
      <button
        className="w-full flex items-center justify-between text-left p-5 hover:bg-cream transition-colors"
        onClick={() => setOpen(!open)}
        data-testid={`faq-toggle-${i}`}
      >
        <span className="text-navy font-medium">{q}</span>
        <ChevronDown
          size={18}
          strokeWidth={2}
          className={`text-ink-muted transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="px-5 pb-5 text-sm text-ink leading-relaxed" data-testid={`faq-answer-${i}`}>
          {a}
        </div>
      )}
    </div>
  );
}

export default function Pricing() {
  return (
    <PageContainer testid="pricing-page" wide>
      <PageHeader
        overline="Pricing"
        title="Four tiers, one operating system."
        subtitle="Every tier is a range. Where you land depends on scope — not on negotiation. To reduce price, we subtract deliverables."
      />

      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-16" data-testid="pricing-tiers">
        {PRICING_TIERS.map((t) => (
          <TierCard key={t.id} tier={t} />
        ))}
      </section>

      {/* How pricing scales */}
      <section className="mb-16" data-testid="pricing-scale-section">
        <div className="overline mb-2">How pricing scales</div>
        <h2 className="text-navy text-2xl font-semibold tracking-tightest mb-5">
          What moves the number within each tier.
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {PRICING_TIERS.map((t) => (
            <ScaleCard key={t.id} tier={t} />
          ))}
        </div>
        <div className="card p-6 mt-6 bg-teal-light/30 border-l-4 border-teal" data-testid="pricing-callout">
          <div className="overline mb-1">How we quote in practice</div>
          <p className="text-navy leading-relaxed">
            In discovery calls, we quote the midpoint. It gives us room to move either direction based on what we
            learn. If a prospect pushes back, we do not drop price arbitrarily — we subtract scope. That protects
            the value of the top of the range for future clients.
          </p>
        </div>
      </section>

      {/* Add-ons */}
      <section className="mb-16" data-testid="pricing-addons-section">
        <div className="overline mb-2">Add-ons</div>
        <h2 className="text-navy text-2xl font-semibold tracking-tightest mb-5">
          Stackable on any tier.
        </h2>
        <div className="grid md:grid-cols-2 gap-3">
          {PRICING_ADDONS.map((a) => (
            <div key={a.name} className="card p-4 flex items-center justify-between gap-3">
              <div className="text-sm text-navy">{a.name}</div>
              <div className="text-sm font-semibold text-teal whitespace-nowrap">{a.price}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="mb-14" data-testid="pricing-faq">
        <div className="overline mb-2">Frequently asked</div>
        <h2 className="text-navy text-2xl font-semibold tracking-tightest mb-5">
          Questions we hear on discovery calls.
        </h2>
        <div className="space-y-3">
          {PRICING_FAQ.map((f, i) => (
            <FAQItem key={f.q} q={f.q} a={f.a} i={i} />
          ))}
        </div>
      </section>

      {/* Not sure */}
      <section className="rounded-xl border border-navy shadow-card p-8 lg:p-10 bg-navy text-cream" data-testid="pricing-not-sure">
        <div className="grid lg:grid-cols-3 gap-6 items-center">
          <div className="lg:col-span-2">
            <div className="overline text-teal-light mb-2">Not sure which tier</div>
            <h2 className="text-2xl lg:text-3xl font-semibold tracking-tightest text-cream">
              A 20-minute call maps you to the right one.
            </h2>
          </div>
          <Link to="/book-call" className="btn btn-primary justify-center" data-testid="pricing-final-cta">
            Book a discovery call <ArrowRight size={16} strokeWidth={2} />
          </Link>
        </div>
      </section>
    </PageContainer>
  );
}
